import React, { useEffect, useState } from "react";
import { Star, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fetchAllFeedback, deleteFeedback } from "../lib/api";
import VoiceCoachPreview from "../components/VoiceCoachPreview";
import AdminShell from "../components/AdminShell";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const RatingStars = ({ rating }) => {
  if (!rating)
    return <span className="text-xs text-white/40">No rating</span>;
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= rating ? "fill-[#D4B483] stroke-[#D4B483]" : "stroke-white/20"
          }`}
        />
      ))}
    </span>
  );
};

const SummaryStat = ({ label, value, testid }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-4"
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
      {label}
    </div>
    <div className="text-3xl font-light text-white tabular-nums">{value}</div>
  </div>
);

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllFeedback();
      setItems(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setError("unauthorized");
      else if (status === 403) setError("forbidden");
      else setError("network");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await deleteFeedback(id);
      setItems((prev) => prev.filter((f) => f.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const avgRating =
    items.filter((i) => i.rating).reduce((s, i) => s + i.rating, 0) /
    Math.max(1, items.filter((i) => i.rating).length);

  return (
    <AdminShell
      eyebrow="Feedback"
      title="What people are saying"
      subtitle="Freshest on top. Star ratings + free-text submissions from the consumer app. Use the drag-mom voice preview below to verify TTS still works after a deploy."
      actions={
        <button
          onClick={load}
          className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white rounded-full px-4 py-2 transition-all text-sm"
          data-testid="admin-feedback-refresh-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }
    >
      <div data-testid="admin-feedback">
        {loading && (
          <div className="text-sm text-white/60" data-testid="admin-feedback-loading">
            Reading the inbox…
          </div>
        )}

        {error === "network" && (
          <div
            className="bg-[#7F2A2A]/20 border border-[#B85F5F]/40 text-[#F4B8C0] rounded-2xl p-5 text-sm"
            data-testid="admin-feedback-network-error"
          >
            Couldn't load feedback. Please refresh.
          </div>
        )}

        {!loading && !error && (
          <>
            <VoiceCoachPreview
              phrase="Drag-mom voice is online. Test phrase from the admin inbox."
              className="mb-6"
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
              <SummaryStat
                label="Total"
                value={items.length}
                testid="admin-feedback-total-stat"
              />
              <SummaryStat
                label="Rated"
                value={items.filter((i) => i.rating).length}
              />
              <SummaryStat
                label="Avg rating"
                value={
                  items.filter((i) => i.rating).length
                    ? avgRating.toFixed(1)
                    : "—"
                }
              />
            </div>

            {items.length === 0 ? (
              <div
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-10 text-center"
                data-testid="admin-feedback-empty"
              >
                <p className="text-white/60">
                  No feedback yet. Share the app with someone — when they
                  leave a note, it'll land here.
                </p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-feedback-list">
                {items.map((f) => (
                  <div
                    key={f.id}
                    className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
                    data-testid={`admin-feedback-row-${f.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <RatingStars rating={f.rating} />
                        <span className="text-xs text-white/50">
                          {formatDate(f.created_at)}
                        </span>
                        {f.page_url && (
                          <span className="text-xs bg-white/10 text-white/80 rounded-full px-2.5 py-0.5 max-w-xs truncate">
                            {f.page_url}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => onDelete(f.id)}
                        className="text-white/40 hover:text-[#F4B8C0] transition-colors p-1"
                        aria-label="Delete feedback"
                        data-testid={`admin-feedback-delete-${f.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <p className="text-white leading-relaxed whitespace-pre-wrap">
                      {f.message}
                    </p>

                    {(f.user_email || f.email) && (
                      <div className="text-xs text-white/50 mt-3 pt-3 border-t border-white/10">
                        From{" "}
                        <span className="text-white/80">
                          {f.user_email || f.email}
                        </span>
                        {f.user_id ? " · signed in" : " · guest"}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminShell>
  );
}
